import { useEffect, useState, type ChangeEvent, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import api, { ApiError } from '../lib/apiClient';

// TS port of the source (Falah) app's pages/EventForm.jsx — ported as-is.
// Matches EventController::validated() exactly.
interface EventFormState {
  title: string;
  description: string;
  start_at: string;
  end_at: string;
  is_public_holiday: boolean;
}

const emptyForm: EventFormState = {
  title: '',
  description: '',
  start_at: '',
  end_at: '',
  is_public_holiday: false,
};

interface EventRecord {
  title: string;
  description: string | null;
  start_at: string;
  end_at: string | null;
  is_public_holiday: boolean;
}

const toInputValue = (s: string | null | undefined) => (s ? s.slice(0, 10) : '');

type FormErrors = Record<string, string[]>;

export function EventForm() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = !id;
  const [form, setForm] = useState<EventFormState>(emptyForm);
  const [errors, setErrors] = useState<FormErrors>({});
  const [loaded, setLoaded] = useState(isNew);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isNew || !id) return;
    api.get<EventRecord>(`/events/${id}`).then((res) => {
      const e = res.data;
      setForm({
        title: e.title,
        description: e.description ?? '',
        start_at: toInputValue(e.start_at),
        end_at: toInputValue(e.end_at),
        is_public_holiday: e.is_public_holiday,
      });
      setLoaded(true);
    });
  }, [id, isNew]);

  const set =
    (key: keyof EventFormState) =>
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm({ ...form, [key]: e.target.value });

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      if (isNew) await api.post('/events', form);
      else await api.put(`/events/${id}`, form);
      navigate('/events');
    } catch (err) {
      setErrors(
        err instanceof ApiError && err.errors ? err.errors : { general: [err instanceof ApiError ? err.message : 'Save failed.'] }
      );
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) return null;

  return (
    <>
      <header className="page-header">
        <h1>{isNew ? 'Add event' : 'Edit event'}</h1>
      </header>

      <div className="card">
        <form onSubmit={save} className="form-grid">
          {errors.general && <div className="alert alert-error span-2">{errors.general[0]}</div>}
          <label className="span-2">
            Title *
            <input value={form.title} onChange={set('title')} required />
            {errors.title && <small className="error">{errors.title[0]}</small>}
          </label>
          <label className="span-2">
            Description
            <textarea rows={2} value={form.description} onChange={set('description')} />
          </label>
          <label>
            Starts *
            <input type="date" value={form.start_at} onChange={set('start_at')} required />
            {errors.start_at && <small className="error">{errors.start_at[0]}</small>}
          </label>
          <label>
            Ends
            <input type="date" value={form.end_at} onChange={set('end_at')} />
            {errors.end_at && <small className="error">{errors.end_at[0]}</small>}
          </label>
          <label className="span-2 checkbox-label">
            <input
              type="checkbox"
              checked={form.is_public_holiday}
              onChange={(e) => setForm({ ...form, is_public_holiday: e.target.checked })}
            />
            Public Holiday
          </label>
          <small className="muted span-2">
            Public holidays are excluded from leave day counts automatically.
          </small>
          <div className="form-actions span-2">
            <Link className="btn btn-ghost" to="/events">
              Cancel
            </Link>
            <button className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}
